import { CoreArchiveDialog } from "@/components/core-archive-dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Loader2, Archive, Plus, AlertTriangle, ChevronDown, MapPin, X, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { AddressInput, cleanupPacContainers } from "@/components/form-fields/address-input";

// Address data interface matching AddressInput component
interface AddressData {
  unitNumber?: string;
  streetNumber?: string;
  streetName?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  formattedAddress?: string;
  latitude?: string;
  longitude?: string;
  placeId?: string;
  isVerified?: number;
}
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { insertContactSchema, insertClientGroupSchema, type Contact, type ClientGroup, type Partner } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PartnerFormModal } from "@/components/partner-form-modal";
import { z } from "zod";

// Mobile number format standardization helper
const formatMobileNumber = (mobile: string, isOverseas: boolean): string => {
  if (!mobile) return "";

  // Remove all spaces, hyphens, parentheses
  const cleaned = mobile.replace(/[\s\-()]/g, '');

  if (isOverseas) {
    // Keep international format with country code
    // Ensure starts with +
    return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
  } else {
    // Australian format: 10 digits, remove country code
    // Remove +61 prefix if present
    const withoutCountryCode = cleaned.replace(/^\+?61/, '');
    // Ensure leading 0 for Australian mobiles
    return withoutCountryCode.startsWith('0') ? withoutCountryCode : `0${withoutCountryCode}`;
  }
};

// Contact form schema - extends insertContactSchema with additional validation
// Override nullable fields with required non-nullable fields for form validation
const contactFormSchema = insertContactSchema
  .omit({
    firstName: true, lastName: true,
    // This form does not edit identity verification. Omit these fields even
    // when an older cached list or a detail record supplies them.
    idVerificationStatus: true, idDocumentType: true, idDocumentNumber: true,
    idDocumentExpiry: true, idVerifiedAt: true, idVerifiedBy: true,
  })
  .extend({
    clientGroupId: z.string().min(1, "Client group is required"),
    firstName: z.string().min(1, "First name is required").max(100, "First name must be less than 100 characters"),
    lastName: z.string().min(1, "Last name is required").max(100, "Last name must be less than 100 characters"),
    leadSource: z.enum([
      "website",
      "referral",
      "cold_call",
      "email_campaign",
      "social_media",
      "event",
      "partner",
      "existing_client",
      "other"
    ]).optional().nullable(),
    mobile: z.string().optional().nullable().refine(
      (val) => !val || /^[\d\s+\-()]*$/.test(val),
      { message: "Mobile number can only contain digits, spaces, +, -, ()" }
    ),
    isOverseasMobile: z.boolean().optional().default(false)
  });

type ContactFormData = z.infer<typeof contactFormSchema>;
type ClientGroupFormData = z.infer<typeof insertClientGroupSchema>;

interface ContactFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: Contact;
  defaultClientGroupId?: string;
  onContactCreated?: (contact: Contact) => void;
}

// Contact types are auto-assigned based on context
// Default: "individual" for all contacts created from client groups/contacts module
// Future: "business_contact" for business partners (Partners module - if implemented)

const LEAD_SOURCES = [
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "cold_call", label: "Cold Call" },
  { value: "email_campaign", label: "Email Campaign" },
  { value: "social_media", label: "Social Media" },
  { value: "event", label: "Event" },
  { value: "partner", label: "Partner" },
  { value: "existing_client", label: "Existing Client" },
  { value: "other", label: "Other" },
];

export function ContactFormModal({ open, onOpenChange, contact, defaultClientGroupId, onContactCreated }: ContactFormModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const isEdit = !!contact;
  const isGroupContext = !!defaultClientGroupId; // Detect if modal opened from client group context
  const [archivePending, setArchivePending] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [showCreatePartnerDialog, setShowCreatePartnerDialog] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [emailToCheck, setEmailToCheck] = useState<string>("");
  const [duplicateContact, setDuplicateContact] = useState<Contact | null>(null);

  // F007: Duplicate detection state
  interface DuplicateResult {
    contactId: string;
    firstName: string;
    lastName: string;
    email: string | null;
    mobile: string | null;
    confidence: number;
    matchReason: string;
  }
  const [duplicateResults, setDuplicateResults] = useState<DuplicateResult[]>([]);
  const [isDuplicateDismissed, setIsDuplicateDismissed] = useState(false);
  const [duplicateCheckParams, setDuplicateCheckParams] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
  } | null>(null);

  // Address integration state (ISSUE-054)
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [useManualAddressEntry, setUseManualAddressEntry] = useState(false);
  const [hasGoogleMapsApi, setHasGoogleMapsApi] = useState(false);
  const [googleApiError, setGoogleApiError] = useState(false);
  const [manualAddress, setManualAddress] = useState({
    unitNumber: "",
    streetNumber: "",
    streetName: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "Australia",
  });

  // Check for Google Maps API on mount
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    setHasGoogleMapsApi(!!apiKey);
    if (!apiKey) {
      setUseManualAddressEntry(true);
    }
  }, []);

  // ISSUE-054 Rework #2: Handle Google API errors by auto-switching to manual entry
  const handleGoogleApiError = () => {
    setGoogleApiError(true);
    setUseManualAddressEntry(true);
  };

  // Fetch client groups for dropdown
  const { data: clientGroups, isLoading: isLoadingGroups, error: groupsError } = useQuery<ClientGroup[]>({
    queryKey: ["/api/crm/client-groups"],
  });

  // ISSUE-163 FIX: Fetch the specific client group to get its referring partner
  const { data: defaultClientGroup } = useQuery<ClientGroup>({
    queryKey: ["/api/crm/client-groups", defaultClientGroupId],
    enabled: open && !!defaultClientGroupId,
  });

  // Fetch partners for dropdown
  const { data: partnersData } = useQuery<Partner[]>({
    queryKey: ["/api/crm/partners"],
    enabled: open,
  });
  const partners = Array.isArray(partnersData) ? partnersData : [];

  // Find currently linked partner
  const linkedPartner = isEdit && contact?.partnerId
    ? partners.find((p) => p.id === contact.partnerId)
    : null;

  // Check for duplicate email (non-blocking warning)
  const { data: allContacts } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts"],
    enabled: open && emailToCheck.length > 0,
  });

  // Find duplicate email contacts
  useEffect(() => {
    if (emailToCheck && allContacts) {
      const duplicate = allContacts.find(
        c => c.email?.toLowerCase() === emailToCheck.toLowerCase() && c.id !== contact?.id
      );
      setDuplicateContact(duplicate || null);
    } else {
      setDuplicateContact(null);
    }
  }, [emailToCheck, allContacts, contact?.id]);

  // F007: Fetch duplicate results when check params change
  // Keep personal fields in the JSON body, while retaining the same cache key.
  const { data: apiDuplicateData } = useQuery<{ duplicates: Array<{ contactId: string; firstName: string; lastName: string; email: string | null; mobile: string | null; confidence: number; matchReason: string }>; hasDuplicates: boolean }>({
    queryKey: [
      "/api/crm/contacts/check-duplicates",
      duplicateCheckParams
        ? { ...duplicateCheckParams, ...(contact?.id ? { excludeId: contact.id } : {}) }
        : null,
    ],
    queryFn: async ({ queryKey }) => {
      const response = await apiRequest("POST", "/api/crm/contacts/check-duplicates", queryKey[1]);
      return response.json();
    },
    enabled: !!duplicateCheckParams && !!duplicateCheckParams.firstName && !!duplicateCheckParams.lastName && open,
    staleTime: 5000,
  });

  // Update duplicate results when API returns data
  useEffect(() => {
    if (apiDuplicateData && !isDuplicateDismissed) {
      setDuplicateResults(apiDuplicateData.duplicates ?? []);
    }
  }, [apiDuplicateData, isDuplicateDismissed]);

  // Reset dismissed state when params change
  useEffect(() => {
    if (duplicateCheckParams) {
      setIsDuplicateDismissed(false);
      setDuplicateResults([]);
    }
  }, [duplicateCheckParams]);

  // Debounced trigger for duplicate check on name/email/mobile fields
  const triggerDuplicateCheck = (firstName: string, lastName: string, email: string, mobile: string) => {
    if (!firstName.trim() || !lastName.trim()) {
      setDuplicateCheckParams(null);
      setDuplicateResults([]);
      return;
    }
    // ISS-005: Convert empty/whitespace-only strings to empty so the query
    // function skips them, preventing false-positive duplicate matches on blank fields
    setDuplicateCheckParams({
      firstName,
      lastName,
      email: email.trim(),
      mobile: mobile.trim(),
    });
  };

  // Form for creating new client group
  const createGroupForm = useForm<ClientGroupFormData>({
    resolver: zodResolver(insertClientGroupSchema),
    defaultValues: {
      groupName: "",
      description: "",
    },
  });

  const defaultValues = {
    clientGroupId: defaultClientGroupId || "",
    contactType: "individual" as const,
    firstName: "",
    middleName: "",
    lastName: "",
    preferredName: "",
    email: "",
    mobile: "",
    isOverseasMobile: false,
    phone: "",
    streetAddress: "",
    suburb: "",
    state: "",
    postcode: "",
    leadSource: undefined as "website" | "referral" | "cold_call" | "email_campaign" | "social_media" | "event" | "partner" | "existing_client" | "other" | undefined,
    tags: [] as string[],
    notes: "",
    partnerId: null as string | null,
  };

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues,
  });

  // Normalize database record for form (omit fields not in schema)
  // Ensure required fields are non-null for form validation
  const normalizeForForm = (record: Contact): ContactFormData => {
    const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, createdBy: _createdBy, ...formData } = record;
    return {
      ...formData,
      firstName: record.firstName || "",
      lastName: record.lastName || "",
      isOverseasMobile: record.isOverseasMobile ?? false,
      tags: record.tags || [],
    };
  };

  useEffect(() => {
    if (open) {
      if (contact) {
        const formData = normalizeForForm(contact);
        form.reset(formData);
        // Initialize selected partner from linked partner OR from contact.partnerId directly
        const partnerIdToUse = linkedPartner?.id || contact.partnerId || null;
        setSelectedPartnerId(partnerIdToUse);
      } else {
        form.reset(defaultValues);
        setSelectedPartnerId(null);

        // Pre-fill client group when context provided
        if (defaultClientGroupId) {
          form.setValue("clientGroupId", defaultClientGroupId, {
            shouldValidate: true,
            shouldDirty: false,
            shouldTouch: true
          });
        }
      }
    }
  }, [open, contact, form, linkedPartner, defaultClientGroupId]);

  // ISSUE-163 FIX: Auto-populate referring partner from client group context
  useEffect(() => {
    if (open && !isEdit && isGroupContext && defaultClientGroup?.referringPartnerId) {
      setSelectedPartnerId(defaultClientGroup.referringPartnerId);
      form.setValue("partnerId", defaultClientGroup.referringPartnerId);
    }
  }, [open, isEdit, isGroupContext, defaultClientGroup, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (archivePending) return;
    if (!newOpen) {
      form.reset(defaultValues);
      // Reset address state (ISSUE-054)
      setAddressData(null);
      setGoogleApiError(false); // Reset API error state on close
      setManualAddress({
        unitNumber: "",
        streetNumber: "",
        streetName: "",
        suburb: "",
        state: "",
        postcode: "",
        country: "Australia",
      });
      if (hasGoogleMapsApi) {
        setUseManualAddressEntry(false);
      }
      // ISSUE-054 Rework #3: Clean up pac-containers when modal closes
      cleanupPacContainers();
      // F007: Reset duplicate detection state
      setDuplicateResults([]);
      setIsDuplicateDismissed(false);
      setDuplicateCheckParams(null);
    }
    onOpenChange(newOpen);
  };

  // ISSUE-054: Helper to build formatted address from manual entry
  const buildFormattedAddress = () => {
    const parts: string[] = [];

    // Build street line with proper handling of optional fields
    const streetParts: string[] = [];
    if (manualAddress.unitNumber?.trim()) {
      streetParts.push(`${manualAddress.unitNumber.trim()}/`);
    }
    if (manualAddress.streetNumber?.trim()) {
      streetParts.push(manualAddress.streetNumber.trim());
    }
    if (manualAddress.streetName?.trim()) {
      if (streetParts.length > 0) {
        streetParts.push(` ${manualAddress.streetName.trim()}`);
      } else {
        streetParts.push(manualAddress.streetName.trim());
      }
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(""));
    }

    // Build location line
    const locationParts = [
      manualAddress.suburb?.trim(),
      manualAddress.state?.trim(),
      manualAddress.postcode?.trim(),
    ].filter(Boolean);

    if (locationParts.length > 0) {
      parts.push(locationParts.join(" "));
    }

    // Add country only if not Australia
    if (manualAddress.country?.trim() && manualAddress.country.trim().toLowerCase() !== "australia") {
      parts.push(manualAddress.country.trim());
    }

    return parts.join(", ");
  };

  // ISSUE-054: Handle address selection from Google autocomplete
  const handleAddressSelect = (data: AddressData) => {
    setAddressData(data);
    setUseManualAddressEntry(false);
  };

  const createMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      const response = await apiRequest("POST", "/api/crm/contacts", data);
      return response.json();
    },
    onSuccess: async (newContact: Contact) => {
      // ISSUE-054: Create address record and link to contact if address data provided
      const hasAddressData = addressData || (useManualAddressEntry && manualAddress.streetName);
      if (hasAddressData) {
        try {
          // Prepare address data from either Google autocomplete or manual entry
          const addressPayload = addressData || {
            unitNumber: manualAddress.unitNumber || undefined,
            streetNumber: manualAddress.streetNumber || undefined,
            streetName: manualAddress.streetName || undefined,
            suburb: manualAddress.suburb || undefined,
            state: manualAddress.state || undefined,
            postcode: manualAddress.postcode || undefined,
            country: manualAddress.country || "Australia",
            formattedAddress: buildFormattedAddress(),
            isVerified: 0,
          };

          // Create address record
          const addressResponse = await apiRequest("POST", "/api/crm/addresses", addressPayload);
          const newAddress = await addressResponse.json();

          // Link address to contact via junction table
          await apiRequest("POST", `/api/crm/contacts/${newContact.id}/addresses`, {
            addressId: newAddress.id,
            addressType: "residential",
            isPrimary: 1,
          });
        } catch (error: any) {
          toast({
            title: "Warning",
            description: `Contact created but failed to save address: ${error.message}`,
            variant: "destructive",
          });
        }
      }

      // Refetch all contact queries (including search variants)
      queryClient.refetchQueries({
        predicate: (query) => query.queryKey[0] === "/api/crm/contacts"
      });
      toast({
        title: "Success",
        description: "Contact created successfully",
      });
      handleOpenChange(false);

      // Call callback if provided (nested creation)
      if (onContactCreated) {
        onContactCreated(newContact);
      } else {
        // Auto-navigate to contact detail page (if not nested creation)
        navigate(`/contacts/${newContact.id}`);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create contact",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      const response = await apiRequest("PATCH", `/api/crm/contacts/${contact?.id}`, data);
      return response.json();
    },
    onSuccess: async () => {
      // Refetch all contact queries (including search variants)
      queryClient.refetchQueries({
        predicate: (query) => query.queryKey[0] === "/api/crm/contacts"
      });
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update contact",
        variant: "destructive",
      });
    },
  });



  const createGroupMutation = useMutation({
    mutationFn: async (data: ClientGroupFormData): Promise<ClientGroup> => {
      const response = await apiRequest("POST", "/api/crm/client-groups", data);
      return response.json();
    },
    onSuccess: (newGroup: ClientGroup) => {
      // Manually update the cache to add the new group without refetching
      // Use a Set to prevent duplicates by ID
      queryClient.setQueryData<ClientGroup[]>(["/api/crm/client-groups"], (oldData) => {
        if (!oldData) return [newGroup];
        // Check if group already exists (prevent duplicates)
        const exists = oldData.some(g => g.id === newGroup.id);
        return exists ? oldData : [...oldData, newGroup];
      });
      // Set the form value with validation
      form.setValue("clientGroupId", newGroup.id, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      setShowCreateGroupDialog(false);
      createGroupForm.reset();
      toast({
        title: "Success",
        description: "Client group created and selected",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create client group",
        variant: "destructive",
      });
    },
  });


  const onSubmit = (data: ContactFormData) => {
    // Format mobile number based on overseas flag
    const formattedMobile = data.mobile
      ? formatMobileNumber(data.mobile, data.isOverseasMobile || false)
      : null;

    // Convert empty strings to null for optional fields (keep clientGroupId as-is since it's required)
    // Keep tags as empty array instead of null to match schema expectations
    const payload = {
      ...data,
      middleName: data.middleName || null,
      preferredName: data.preferredName || null,
      email: data.email || null,
      mobile: formattedMobile,
      isOverseasMobile: data.isOverseasMobile || false,
      phone: data.phone || null,
      streetAddress: data.streetAddress || null,
      suburb: data.suburb || null,
      state: data.state || null,
      postcode: data.postcode || null,
      leadSource: data.leadSource || null,
      tags: data.tags && data.tags.length > 0 ? data.tags : [],
      notes: data.notes || null,
      partnerId: selectedPartnerId || null,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };



  const handlePartnerChange = (partnerId: string) => {
    const newPartnerId = partnerId === "none" ? null : partnerId;
    setSelectedPartnerId(newPartnerId);

    // Update the form value so it persists when the form is saved
    form.setValue("partnerId", newPartnerId);

    // Don't call immediate mutation - let the form save handle it
    // This prevents the form from being reset when the query refetches
  };

  const handlePartnerCreated = (newPartner: Partner) => {
    // Update cache to include new partner
    queryClient.setQueryData<Partner[]>(["/api/crm/partners"], (oldData) => {
      if (!oldData) return [newPartner];
      const exists = oldData.some(p => p.id === newPartner.id);
      return exists ? oldData : [...oldData, newPartner];
    });

    // Set selected partner ID
    setSelectedPartnerId(newPartner.id);

    // Update the form value
    form.setValue("partnerId", newPartner.id);

    // Don't call immediate mutation - let the form save handle it
    // When creating a new contact, partnerId will be included in the create payload
    // When editing, the form submit will save the partnerId

    setShowCreatePartnerDialog(false);
  };

  const isPending = createMutation.isPending || updateMutation.isPending || archivePending || createGroupMutation.isPending;
  const canSubmit = !isPending && !isLoadingGroups && clientGroups && clientGroups.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          // Prevent dialog from closing when clicking Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Contact" : "Add Contact"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the contact information below"
              : "Enter the details for the new contact"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Client Group */}
              <FormField
                control={form.control}
                name="clientGroupId"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <div className="flex items-center justify-between">
                      <FormLabel>Client Group *</FormLabel>
                      {!isGroupContext && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCreateGroupDialog(true)}
                          disabled={createGroupMutation.isPending}
                          data-testid="button-create-client-group"
                        >
                          <Plus className="mr-1 h-4 w-4" />
                          New Group
                        </Button>
                      )}
                    </div>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                      disabled={isLoadingGroups || isGroupContext}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-client-group">
                          <SelectValue
                            placeholder={
                              isLoadingGroups
                                ? "Loading client groups..."
                                : groupsError
                                  ? "Error loading client groups"
                                  : "Select client group"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {clientGroups && clientGroups.length > 0 ? (
                          clientGroups.map((group) => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.groupName}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-groups" disabled>
                            No client groups available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {isGroupContext
                        ? "This contact will be created in the current client group"
                        : "Every contact must belong to a client group"}
                    </FormDescription>
                    {groupsError && (
                      <p className="text-sm text-destructive">
                        Failed to load client groups. Please try again.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Referring Partner */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Referring Partner (Optional)
                  </label>
                  {!isGroupContext && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCreatePartnerDialog(true)}
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      New Partner
                    </Button>
                  )}
                </div>
                {isGroupContext ? (
                  <Input
                    value={partners.find(p => p.id === selectedPartnerId)?.partnerName || ""}
                    disabled
                    placeholder="No partner"
                    data-testid="referring-partner-field"
                    className="bg-muted"
                  />
                ) : (
                  <Select
                    onValueChange={handlePartnerChange}
                    value={selectedPartnerId || undefined}
                    disabled={isGroupContext}
                  >
                    <SelectTrigger data-testid="select-referring-partner">
                      <SelectValue placeholder="Select partner (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No partner</SelectItem>
                      {partners.map((partner) => (
                        <SelectItem key={partner.id} value={partner.id}>
                          {partner.partnerName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  {isGroupContext
                    ? "Referring partner is inherited from the client group context"
                    : "Link this contact to a referring partner"}
                </p>
              </div>

              {/* Contact Type - Auto-assigned to "individual" (MT-004 fix)
                  No dropdown needed - context determines type automatically
                  Future: Partners module can create "business_contact" type
              */}

              {/* First Name */}
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., John"
                        data-testid="input-first-name"
                        {...field}
                        value={field.value || ""}
                        onBlur={(e) => {
                          field.onBlur();
                          const firstName = e.target.value.trim();
                          const lastName = form.getValues("lastName")?.trim() ?? "";
                          const email = form.getValues("email") ?? "";
                          const mobile = form.getValues("mobile") ?? "";
                          setTimeout(() => triggerDuplicateCheck(firstName, lastName, email, mobile), 500);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Last Name */}
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Smith"
                        data-testid="input-last-name"
                        {...field}
                        value={field.value || ""}
                        onBlur={(e) => {
                          field.onBlur();
                          const lastName = e.target.value.trim();
                          const firstName = form.getValues("firstName")?.trim() ?? "";
                          const email = form.getValues("email") ?? "";
                          const mobile = form.getValues("mobile") ?? "";
                          setTimeout(() => triggerDuplicateCheck(firstName, lastName, email, mobile), 500);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* F007: Duplicate detection alert — shown when matching contacts found */}
              {duplicateResults.length > 0 && !isDuplicateDismissed && (
                <div
                  className="md:col-span-2 rounded-md border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3"
                  data-testid="alert-duplicates"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                      <span className="text-sm font-medium text-amber-900 dark:text-amber-300">
                        Potential duplicate contacts found
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800"
                      onClick={() => setIsDuplicateDismissed(true)}
                      data-testid="button-dismiss-duplicates"
                    >
                      <X className="h-3.5 w-3.5" />
                      <span className="sr-only">Dismiss</span>
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {duplicateResults.map((dup) => (
                      <div
                        key={dup.contactId}
                        className="flex items-center justify-between gap-2 rounded border border-amber-200 dark:border-amber-700 bg-white dark:bg-amber-900/30 px-2 py-1.5"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                            {dup.firstName} {dup.lastName}
                          </p>
                          {(dup.email || dup.mobile) && (
                            <p className="text-xs text-amber-700 dark:text-amber-400 truncate">
                              {dup.email ?? dup.mobile}
                            </p>
                          )}
                          {/* Confidence bar */}
                          <div className="mt-1 flex items-center gap-2" data-testid="duplicate-confidence">
                            <div className="flex-1 h-1.5 rounded-full bg-amber-200 dark:bg-amber-700 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${dup.confidence >= 0.8 ? "bg-green-500" : "bg-amber-500"}`}
                                style={{ width: `${Math.round(dup.confidence * 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-amber-700 dark:text-amber-400 shrink-0">
                              {Math.round(dup.confidence * 100)}%
                            </span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-amber-700 dark:text-amber-300 shrink-0"
                          onClick={() => window.open(`/contacts/${dup.contactId}`, "_blank")}
                        >
                          View Contact
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-800"
                    onClick={() => setIsDuplicateDismissed(true)}
                    data-testid="button-continue-anyway"
                  >
                    Continue Anyway
                  </Button>
                </div>
              )}

              {/* Middle Name */}
              <FormField
                control={form.control}
                name="middleName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Middle Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Optional"
                        data-testid="input-middle-name"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Preferred Name */}
              <FormField
                control={form.control}
                name="preferredName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Optional"
                        data-testid="input-preferred-name"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="e.g., john.smith@example.com"
                        data-testid="input-email"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => {
                          field.onChange(e);
                          // Debounce email check (legacy)
                          const email = e.target.value;
                          if (email && email.includes("@")) {
                            setTimeout(() => setEmailToCheck(email), 500);
                          } else {
                            setEmailToCheck("");
                          }
                        }}
                        onBlur={(e) => {
                          field.onBlur();
                          const email = e.target.value;
                          const firstName = form.getValues("firstName")?.trim() ?? "";
                          const lastName = form.getValues("lastName")?.trim() ?? "";
                          const mobile = form.getValues("mobile") ?? "";
                          setTimeout(() => triggerDuplicateCheck(firstName, lastName, email, mobile), 500);
                        }}
                      />
                    </FormControl>
                    {duplicateContact && (
                      <div className="mt-2 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 rounded-md">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-orange-900 dark:text-orange-400">
                              Possible Duplicate
                            </p>
                            <p className="text-xs text-orange-800 dark:text-orange-500 mt-1">
                              Another contact exists with this email: {duplicateContact.firstName} {duplicateContact.lastName}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/contacts/${duplicateContact.id}`, "_blank")}
                            className="shrink-0"
                          >
                            View Contact
                          </Button>
                        </div>
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Mobile */}
              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., 0400 123 456 or +61 400 123 456"
                        data-testid="input-mobile"
                        {...field}
                        value={field.value || ""}
                        onBlur={(e) => {
                          field.onBlur();
                          const mobile = e.target.value;
                          const firstName = form.getValues("firstName")?.trim() ?? "";
                          const lastName = form.getValues("lastName")?.trim() ?? "";
                          const email = form.getValues("email") ?? "";
                          setTimeout(() => triggerDuplicateCheck(firstName, lastName, email, mobile), 500);
                        }}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Can include spaces, +, -, (). Will be standardized on save.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Overseas Mobile Checkbox */}
              <FormField
                control={form.control}
                name="isOverseasMobile"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        data-testid="checkbox-overseas-mobile"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Overseas Mobile</FormLabel>
                      <FormDescription className="text-xs">
                        Check if mobile is international (will keep country code)
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {/* Phone */}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., (02) 1234 5678"
                        data-testid="input-phone"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Lead Source */}
              <FormField
                control={form.control}
                name="leadSource"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lead Source</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-lead-source">
                          <SelectValue placeholder="Select lead source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {LEAD_SOURCES.map((source) => (
                          <SelectItem key={source.value} value={source.value}>
                            {source.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address Section - Collapsible with Google Maps Integration (ISSUE-054) */}
              <div className="md:col-span-2">
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full justify-between mb-2"
                    >
                      <div className="flex items-center text-sm font-medium">
                        <MapPin className="h-4 w-4 mr-2" />
                        Residential Address (Optional)
                      </div>
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 [&[data-state=open]]:rotate-180" />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4">
                    {/* Toggle between search and manual entry */}
                    {hasGoogleMapsApi && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="manualAddressEntryContact"
                          checked={useManualAddressEntry}
                          onChange={(e) => {
                            setUseManualAddressEntry(e.target.checked);
                            if (e.target.checked) {
                              setAddressData(null);
                            }
                          }}
                          className="rounded border-gray-300"
                          data-testid="checkbox-manual-address-entry"
                        />
                        <Label htmlFor="manualAddressEntryContact" className="cursor-pointer text-sm">
                          Enter address manually
                        </Label>
                      </div>
                    )}

                    {useManualAddressEntry ? (
                      <div className="space-y-3">
                        {/* Show info message if switched due to API error */}
                        {googleApiError && (
                          <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-300">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            Google Maps unavailable. Please enter address manually.
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Unit Number</Label>
                            <Input
                              value={manualAddress.unitNumber}
                              onChange={(e) => setManualAddress({ ...manualAddress, unitNumber: e.target.value })}
                              placeholder="e.g., 5"
                              data-testid="input-unit-number"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Street Number</Label>
                            <Input
                              value={manualAddress.streetNumber}
                              onChange={(e) => setManualAddress({ ...manualAddress, streetNumber: e.target.value })}
                              placeholder="e.g., 123"
                              data-testid="input-street-number"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Street Name</Label>
                          <Input
                            value={manualAddress.streetName}
                            onChange={(e) => setManualAddress({ ...manualAddress, streetName: e.target.value })}
                            placeholder="e.g., Main Street"
                            data-testid="input-street-name"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Suburb</Label>
                            <Input
                              value={manualAddress.suburb}
                              onChange={(e) => setManualAddress({ ...manualAddress, suburb: e.target.value })}
                              placeholder="e.g., Sydney"
                              data-testid="input-suburb"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">State</Label>
                            <Select
                              value={manualAddress.state || undefined}
                              onValueChange={(value) => setManualAddress({ ...manualAddress, state: value })}
                            >
                              <SelectTrigger data-testid="select-state">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="NSW">NSW</SelectItem>
                                <SelectItem value="VIC">VIC</SelectItem>
                                <SelectItem value="QLD">QLD</SelectItem>
                                <SelectItem value="WA">WA</SelectItem>
                                <SelectItem value="SA">SA</SelectItem>
                                <SelectItem value="TAS">TAS</SelectItem>
                                <SelectItem value="ACT">ACT</SelectItem>
                                <SelectItem value="NT">NT</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Postcode</Label>
                            <Input
                              value={manualAddress.postcode}
                              onChange={(e) => setManualAddress({ ...manualAddress, postcode: e.target.value })}
                              placeholder="e.g., 2000"
                              maxLength={4}
                              data-testid="input-postcode"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label>Search Address</Label>
                        <AddressInput
                          onAddressSelect={handleAddressSelect}
                          onApiError={handleGoogleApiError}
                          placeholder="Start typing to search..."
                          data-testid="input-address-search"
                        />
                        {addressData && (
                          <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {addressData.formattedAddress || "Address selected"}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Address will be saved to the address book and linked to this contact
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </div>

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any additional notes or context..."
                        className="resize-none"
                        rows={3}
                        data-testid="textarea-notes"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Record status Section - RULE-SAFETY-001 */}
            {isEdit && (
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-between text-destructive hover:text-destructive hover:bg-destructive/10 mt-6"
                    data-testid="button-danger-zone-toggle"
                  >
                    <div className="flex items-center">
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Record status
                    </div>
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 [&[data-state=open]]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-2">
                  <Alert>
                    <AlertDescription className="text-sm">
                      Archive hides this record from active lists. Its linked records and history stay available. You can restore it later.
                    </AlertDescription>
                  </Alert>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowArchiveDialog(true)}
                    disabled={isPending}
                    data-testid="button-archive"
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    {contact?.archivedAt ? "Restore Contact" : "Archive Contact"}
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            )}

            <DialogFooter className="gap-2 mt-6">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid={isEdit ? "button-save" : "button-create-contact"}
                >
                  {(isPending || isLoadingGroups) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEdit ? "Update Contact" : "Create Contact"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {/* Archive confirmation */}
    <CoreArchiveDialog entity="contact" record={contact} open={showArchiveDialog}
      onOpenChange={setShowArchiveDialog} onPendingChange={setArchivePending} onSuccess={() => onOpenChange(false)} />

      {/* Create Client Group Dialog */}
      <Dialog open={showCreateGroupDialog} onOpenChange={setShowCreateGroupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Client Group</DialogTitle>
            <DialogDescription>
              Create a new client group and it will be automatically selected
            </DialogDescription>
          </DialogHeader>

          <Form {...createGroupForm}>
            <form onSubmit={createGroupForm.handleSubmit((data) => createGroupMutation.mutate(data))} className="space-y-4">
              <FormField
                control={createGroupForm.control}
                name="groupName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., ABC Financial Group"
                        data-testid="input-group-name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createGroupForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Optional description of the client group"
                        data-testid="textarea-group-description"
                        className="resize-none"
                        rows={3}
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateGroupDialog(false)}
                  disabled={createGroupMutation.isPending}
                  data-testid="button-cancel-create-group"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createGroupMutation.isPending} data-testid="button-save-create-group">
                  {createGroupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Group
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Create Partner Dialog */}
      <PartnerFormModal
        open={showCreatePartnerDialog}
        onOpenChange={setShowCreatePartnerDialog}
        onPartnerCreated={handlePartnerCreated}
      />
    </Dialog>
  );
}
